<script setup>
import {
  ref,
  computed,
  onMounted,
  watch,
  onActivated,
  onBeforeUnmount,
} from 'vue';
import { onClickOutside, useEventListener } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { onBeforeRouteLeave } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';

import { useBase, dataHora, hora, MONO, CHIP_ESTADO } from './base';

import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import FiltroSelect from './FiltroSelect.vue';

import SandboxAPI from 'dashboard/api/fandata/sandbox';
import FaqsAPI from 'dashboard/api/fandata/faqs';

// T-186, tela 4a — onde a pessoa passa o tempo dela.
//
// Três decisões do desenho mandam aqui:
// 1. O que está NO AR fica ao lado da cópia editável, sempre.
// 2. O texto se salva sozinho; "Salvar resposta" CONFIRMA que aquela resposta está pronta.
// 3. Editar uma resposta gera uma versão da BASE INTEIRA, e a barra diz isso o tempo todo.
const { t } = useI18n();
const { irPara } = useBase();
const usuario = useMapGetter('getCurrentUser');

const ESPERA_AUTOSAVE = 900;

const carregando = ref(true);
const itens = ref([]);
const rascunho = ref({ aberto: false });
const busca = ref('');
const filtroCategoria = ref('');
const categoriasOficiais = ref([]);
const filtroEstado = ref('');

// A gaveta cobre a lista em vez de espremê-la. `ignore` na lista é o que faz clicar noutro
// cartão TROCAR de resposta em vez de fechar e abrir de novo, que pediria a decisão duas vezes.
const gaveta = ref(null);
const lista = ref(null);

const card = ref(null);
const noAr = ref(null);
const historico = ref([]);
const mostrarHistorico = ref(false);
const abertoNumero = ref(null);

const salvando = ref(false);
const sujo = ref(false);
const ultimoSalvamento = ref(null);
const editandoRotulo = ref(false);
const rotulo = ref('');
const conflito = ref(null);
let cronometro = null;

// ⚠️ `reset-base` nos <input> e `m-0 border-solid` nos <select>/<textarea> não são enfeite.
// O `_base.scss` estiliza todo campo de formulário por seletor de elemento com uma pilha de
// `:not()`, e a especificidade disso vence qualquer classe do Tailwind: sem o reset, todo input
// desta tela volta a ter largura cheia, margem de 16px embaixo e `border-none` com outline no
// lugar da borda. Era isso que sumia com as bordas e esticava o campo de busca.

const pendentes = computed(() => {
  const r = rascunho.value;
  return (r.alterados || 0) + (r.novos || 0) + (r.a_sair || 0);
});

const quemMexeu = computed(() =>
  (rascunho.value.por_pessoa || [])
    .map(p => `${p.nome} ${p.respostas}`)
    .join(', ')
);

const alcance = computed(() => {
  const planos = (card.value?.planos || []).filter(Boolean);
  const situacoes = (card.value?.situacoes || []).filter(Boolean);
  if (!planos.length && !situacoes.length) return t('BASE_EDITAR.TODOS');
  return [...planos, ...situacoes].join(', ');
});

// A mesma normalização do Fandata::Categorias, do lado de cá: acento fora, caixa baixa,
// pontuação REMOVIDA (não virando espaço, senão "e-mail" e "email" continuariam separados) e
// espaços colapsados. É o que faz "Beneficios" e "beneficios" serem um grupo só.
const normalizar = valor =>
  (valor || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, ' ');

// O campo da gaveta oferece a lista FECHADA das onze, mais a categoria que a resposta TEM.
//
// A comparação é exata de propósito, e não normalizada: "Ingressos" e "ingressos" são a mesma
// categoria para agrupar, mas são textos diferentes no banco. Se a lista só tivesse a grafia
// oficial, abrir uma resposta escrita com a outra mostraria um campo que não corresponde ao que
// está no ar, e trocar para a oficial viraria uma alteração que ninguém pediu.
const opcoesCategoria = computed(() => {
  const nomes = [...categoriasOficiais.value];
  const atual = card.value?.category;
  if (atual && !nomes.includes(atual)) nomes.push(atual);
  return nomes.map(nome => ({ valor: nome, rotulo: nome }));
});

const ESTADOS = {
  no_ar: ['no_ar'],
  rascunho: ['alterado', 'novo'],
  desativada: ['a_sair'],
};

const opcoesEstado = computed(() => [
  { valor: '', rotulo: t('BASE_EDITAR.ESTADO_FILTRO.TODOS') },
  ...Object.keys(ESTADOS).map(chave => ({
    valor: chave,
    rotulo: t(`BASE_EDITAR.ESTADO_FILTRO.${chave.toUpperCase()}`),
  })),
]);

const visiveis = computed(() => {
  const estados = ESTADOS[filtroEstado.value];
  const chave = filtroCategoria.value;
  return itens.value.filter(item => {
    const passaEstado =
      !estados ||
      estados.includes(item.estado) ||
      (estados.includes('a_sair') && !item.ativo);
    const passaCategoria = !chave || normalizar(item.categoria) === chave;
    return passaEstado && passaCategoria;
  });
});

// A lista é agrupada por categoria porque é assim que quem escreve pensa nela: ninguém procura
// "a resposta 214", procura "aquela de cupom".
//
// Duas regras que só existem por causa do estado da base de hoje, e que somem sozinhas quando a
// base vier com as onze categorias:
// 1. grupo de menos de MINIMO_DO_GRUPO respostas cai em "outros". Sem isso, 20 grafias com uma
//    resposta cada viram 20 cabeçalhos e a tela deixa de ser uma lista.
// 2. o rótulo do grupo é a grafia mais frequente dele, não a primeira que aparecer.
const MINIMO_DO_GRUPO = 3;
const SEM_CATEGORIA = '\u0000sem';
const OUTROS = '\u0000outros';

const agrupar = lista => {
  const mapa = new Map();
  lista.forEach(item => {
    const chave = normalizar(item.categoria) || SEM_CATEGORIA;
    if (!mapa.has(chave)) mapa.set(chave, []);
    mapa.get(chave).push(item);
  });
  return mapa;
};

// O rótulo do grupo é a grafia OFICIAL quando o servidor soube dizer qual é; só cai na mais
// frequente do grupo quando a categoria ainda não tem para onde ir. A chave de agrupamento
// continua sendo a normalizada, que é sem acento: usá-la como rótulo escreveria BENEFICIOS.
const rotuloDoGrupo = itensDoGrupo => {
  const contagem = new Map();
  itensDoGrupo.forEach(item => {
    const nome = item.categoria_oficial || item.categoria;
    if (nome) contagem.set(nome, (contagem.get(nome) || 0) + 1);
  });
  return [...contagem.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
};

const porPergunta = (a, b) => (a.titulo || '').localeCompare(b.titulo || '');

const grupos = computed(() => {
  const mapa = agrupar(visiveis.value);
  const nomeados = [];
  const outros = [];

  mapa.forEach((itensDoGrupo, chave) => {
    if (chave === SEM_CATEGORIA) return;
    if (itensDoGrupo.length < MINIMO_DO_GRUPO)
      return outros.push(...itensDoGrupo);
    return nomeados.push({
      chave,
      nome: rotuloDoGrupo(itensDoGrupo),
      itens: [...itensDoGrupo].sort(porPergunta),
    });
  });

  nomeados.sort((a, b) => a.nome.localeCompare(b.nome));
  const finais = [
    { chave: OUTROS, nome: t('BASE_EDITAR.OUTROS'), itens: outros },
    {
      chave: SEM_CATEGORIA,
      nome: t('BASE_EDITAR.SEM_CATEGORIA'),
      itens: mapa.get(SEM_CATEGORIA) || [],
    },
  ].filter(grupo => grupo.itens.length);

  finais.forEach(grupo => grupo.itens.sort(porPergunta));
  return [...nomeados, ...finais];
});

const opcoesFiltroCategoria = computed(() => [
  { valor: '', rotulo: t('BASE_EDITAR.CATEGORIA_TODAS') },
  ...grupos.value
    .filter(grupo => grupo.chave !== OUTROS && grupo.chave !== SEM_CATEGORIA)
    .map(grupo => ({
      valor: grupo.chave,
      rotulo: grupo.nome,
      contagem: grupo.itens.length,
    })),
]);

const filtrando = computed(() =>
  Boolean(filtroCategoria.value || filtroEstado.value)
);

const limparFiltros = () => {
  filtroCategoria.value = '';
  filtroEstado.value = '';
};

// T-186: o rascunho é um só e compartilhado, sem trava. O que substitui a trava é o aviso: quem
// abre uma resposta que a colega mexeu há pouco precisa saber ANTES de escrever por cima, porque
// escrever por cima é legítimo e vira uma versão anterior com o nome dela.
const mexidaPorOutra = computed(() => {
  const item = itens.value.find(i => i.numero_card === abertoNumero.value);
  if (!item?.alterado_por || !item.alterado_em) return null;
  if (item.alterado_por === usuario.value?.name) return null;
  return item;
});

const opcoesAcao = computed(() => [
  { valor: '', rotulo: '—' },
  ...['responder', 'encerrar', 'transferir'].map(acao => ({
    valor: acao,
    rotulo: t(`BASE_EDITAR.ACAO.${acao.toUpperCase()}`),
  })),
]);

// Indexar é o que faz o Bira ENCONTRAR a resposta. Leva uns 10 minutos numa base de 350 (a
// Voyage no plano gratuito limita 3 requisições por minuto), e sem mostrar isso a espera fica
// invisível: a pessoa testa, o Bira não acha, e a conclusão vira "a base nova não presta".
const indexacao = ref({});
const indexando = computed(() => (indexacao.value.pendentes || 0) > 0);

const carregarLista = async () => {
  // Só a busca vai ao servidor. Categoria e estado filtram aqui porque é aqui que o
  // agrupamento acontece: filtrar no servidor por uma grafia e agrupar por outra, normalizada,
  // faria o filtro e a lista discordarem na mesma tela.
  const { data } = await SandboxAPI.indice({ tipo: 'faq', busca: busca.value });
  itens.value = data.itens || [];
  categoriasOficiais.value = data.categorias || [];
  rascunho.value = data.rascunho || { aberto: false };
  indexacao.value = data.indexacao || {};
  rotulo.value = rascunho.value.rotulo || '';
  // O "salvo às" é do rascunho inteiro, não desta sessão: quem abre a tela de manhã precisa ver
  // a hora em que a colega parou de mexer ontem, e isso só o servidor sabe.
  if (rascunho.value.ultima_escrita)
    ultimoSalvamento.value = rascunho.value.ultima_escrita;
};

// Enquanto indexa, a tela se atualiza sozinha: uma barra que não anda é indistinguível de
// uma travada, e foi assim que a espera virou "não fez nada" na importação.
let relogioIndex = null;
const pararDeAcompanharIndice = () => {
  if (relogioIndex) clearInterval(relogioIndex);
  relogioIndex = null;
};

watch(indexando, ligado => {
  pararDeAcompanharIndice();
  if (ligado) relogioIndex = setInterval(carregarLista, 15000);
});

onBeforeUnmount(pararDeAcompanharIndice);

const carregar = async () => {
  carregando.value = true;
  try {
    await carregarLista();
  } catch (error) {
    useAlert(t('BASE_EDITAR.ERRO'));
  } finally {
    carregando.value = false;
  }
};

const abrirAgora = async numero => {
  abertoNumero.value = numero;
  mostrarHistorico.value = false;
  const { data } = await SandboxAPI.card(numero);
  card.value = data.card;
  noAr.value = data.no_ar;
  historico.value = data.historico || [];
  sujo.value = false;
};

// Sair com alteração pendente não descarta nem salva sozinho: pergunta. O texto já está no
// servidor pelo salvamento automático, então "descartar" aqui significa voltar ao que estava
// no ar, e isso precisa ser uma escolha consciente.
const pedirDecisao = destino => {
  conflito.value = destino;
};

const abrir = item => {
  if (sujo.value) return pedirDecisao(() => abrirAgora(item.numero_card));
  return abrirAgora(item.numero_card);
};

const fecharAgora = () => {
  abertoNumero.value = null;
  card.value = null;
  sujo.value = false;
};

const fechar = () => (sujo.value ? pedirDecisao(fecharAgora) : fecharAgora());

const agendarSalvamento = () => {
  sujo.value = true;
  if (card.value?.nova) return;
  clearTimeout(cronometro);
  cronometro = setTimeout(() => salvar(false), ESPERA_AUTOSAVE);
};

// Editar a base É abrir a próxima versão dela. Enquanto ninguém escreveu nada, não existe
// rascunho aberto, e o endpoint de escrita recusa (`?versao=rascunho` sem rascunho é 422). Quem
// clicou em "Editar base" não devia precisar saber disso: a tela abre sozinha.
//
// Na PRIMEIRA escrita, e não ao entrar, senão visitar a tela criaria versão da base sem ninguém
// ter mexido em nada.
let abertura = null;

// Mesma rede da tela de comportamento: `forcar` é para quando o servidor disse que não há
// rascunho, e a tela achava que havia.
const garantirRascunho = async (forcar = false) => {
  if (rascunho.value.aberto && !forcar) return;

  abertura = abertura || SandboxAPI.abrir();
  await abertura;
  abertura = null;
  // Abrir o rascunho CLONA a base: a linha que está na tela é a que está NO AR, e daqui em
  // diante quem recebe a edição é a cópia. Sem trocar o id aqui, o primeiro salvamento iria
  // parar no que o torcedor está recebendo agora.
  rascunho.value = { ...rascunho.value, aberto: true };
  if (!abertoNumero.value) return;

  const { data } = await SandboxAPI.card(abertoNumero.value);
  card.value = { ...card.value, id: data.card.id };
  noAr.value = data.no_ar;
};

// Criar é explícito, não vem do salvamento automático: resposta nasce quando a pessoa diz que
// terminou de escrevê-la. Autosalvar uma resposta vazia encheria o rascunho de linhas em branco
// que alguém publicaria sem perceber.
const novaResposta = () => {
  if (sujo.value) return pedirDecisao(novaResposta);

  abertoNumero.value = null;
  mostrarHistorico.value = false;
  noAr.value = null;
  historico.value = [];
  sujo.value = false;
  card.value = {
    nova: true,
    question: '',
    answer: '',
    category: '',
    literal: false,
    enabled: true,
    planos: [],
    situacoes: [],
  };
  return null;
};

// T-216 — card NÃO literal precisa de pergunta de triagem e de condição.
//
// Card literal é entregue palavra por palavra e não precisa de mais nada. Quando ele não é
// literal, o Bira ADAPTA o texto, e para adaptar direito precisa saber duas coisas: o que
// confirmar antes de responder (a pergunta de triagem) e para quem aquela resposta vale (a
// condição). Sem elas, card não literal vira resposta genérica.
//
// AVISA, NÃO IMPEDE, e isso foi medido antes de decidir: na base do clube de 15/09, 248 dos 357
// cards estão sem as duas (a planilha nem traz a coluna `literal`, então todos são não
// literais). Impedir o salvamento obrigaria a revisar 69% da base de uma vez, e na prática
// pararia a edição de qualquer card até alguém preencher os outros.
const faltaNoCardNaoLiteral = computed(() => {
  if (!card.value || card.value.literal) return [];
  return [
    card.value.pergunta_triagem?.trim() ? null : 'PERGUNTA_TRIAGEM',
    card.value.condicao?.trim() ? null : 'CONDICAO',
  ].filter(Boolean);
});

const podeSalvar = computed(() => {
  if (!card.value) return false;
  if (!card.value.nova) return sujo.value;
  return Boolean(card.value.question?.trim() && card.value.answer?.trim());
});

const criar = async () => {
  await garantirRascunho();
  const { data } = await FaqsAPI.create(
    {
      question: card.value.question,
      answer: card.value.answer,
      category: card.value.category,
      literal: card.value.literal,
      acao_tipo: card.value.acao_tipo,
      pergunta_triagem: card.value.pergunta_triagem,
      condicao: card.value.condicao,
      planos: card.value.planos || [],
      situacoes: card.value.situacoes || [],
    },
    'rascunho'
  );
  sujo.value = false;
  await carregarLista();
  return abrirAgora(data.numero_card);
};

const escrever = confirmar =>
  FaqsAPI.update(
    card.value.id,
    {
      question: card.value.question,
      answer: card.value.answer,
      category: card.value.category,
      literal: card.value.literal,
      enabled: card.value.enabled,
      acao_tipo: card.value.acao_tipo,
      acao_destino: card.value.acao_destino,
      pergunta_triagem: card.value.pergunta_triagem,
      condicao: card.value.condicao,
      planos: card.value.planos || [],
      situacoes: card.value.situacoes || [],
    },
    'rascunho',
    confirmar
  );

const salvar = async (confirmar = false) => {
  if (!card.value) return;
  if (card.value.nova) {
    salvando.value = true;
    try {
      await criar();
    } catch (error) {
      useAlert(t('BASE_EDITAR.ERRO_SALVAR'));
    } finally {
      salvando.value = false;
    }
    return;
  }
  clearTimeout(cronometro);
  salvando.value = true;
  try {
    await garantirRascunho();
    try {
      await escrever(confirmar);
    } catch (erro) {
      if (erro?.response?.status !== 422) throw erro;
      rascunho.value = { ...rascunho.value, aberto: false };
      await garantirRascunho(true);
      await escrever(confirmar);
    }
    sujo.value = false;
    ultimoSalvamento.value = new Date();
    await carregarLista();
    if (confirmar) fecharAgora();
  } catch (error) {
    useAlert(t('BASE_EDITAR.ERRO_SALVAR'));
  } finally {
    salvando.value = false;
  }
};

const descartar = async () => {
  if (!card.value || !noAr.value) return fecharAgora();
  card.value.answer = noAr.value.answer;
  card.value.question = noAr.value.question;
  await salvar(false);
  return fecharAgora();
};

const resolverConflito = async acao => {
  const destino = conflito.value;
  conflito.value = null;
  if (acao === 'salvar') await salvar(true);
  else await descartar();
  if (destino) destino();
};

// Trazer uma versão antiga de volta era função do editor provisório, que saiu na faxina. O
// endpoint continua sendo o mesmo: a revisão volta para o RASCUNHO, nunca para o ar, e de lá
// segue pelo caminho normal de conferir e publicar.
const trazerDeVolta = async revisao => {
  try {
    await SandboxAPI.reverterCard(revisao.id);
    useAlert(t('BASE_EDITAR.TROUXE'));
    await carregarLista();
    await abrirAgora(abertoNumero.value);
  } catch (error) {
    useAlert(t('BASE_EDITAR.ERRO_TRAZER'));
  }
};

const desativar = async () => {
  if (!card.value) return;
  card.value.enabled = false;
  await salvar(true);
};

const salvarRotulo = async () => {
  editandoRotulo.value = false;
  try {
    await SandboxAPI.salvar({ rotulo: rotulo.value });
    await carregarLista();
  } catch (error) {
    useAlert(t('BASE_EDITAR.ERRO_SALVAR'));
  }
};

// Quase toda a base veio da planilha sem "depois de responder". O proxy deixa o campo em branco
// em vez de assumir a primeira opção do select, que gravaria uma decisão que ninguém tomou.
const acaoTipo = computed({
  get: () => card.value?.acao_tipo || '',
  set: valor => {
    card.value.acao_tipo = valor || null;
    agendarSalvamento();
  },
});

const rotuloAcao = registro => {
  if (!registro?.acao_tipo) return '—';
  const nome = t(`BASE_EDITAR.ACAO.${registro.acao_tipo.toUpperCase()}`);
  return registro.acao_destino ? `${nome} · ${registro.acao_destino}` : nome;
};

const restringindo = ref(false);
// Planos e situações são listas, mas na tela são uma linha de texto separada por vírgula:
// quem edita a base escreve "socio, socio-plus", não monta um multi-select.
const definirLista = (campo, texto) => {
  card.value[campo] = texto
    .split(',')
    .map(v => v.trim())
    .filter(Boolean);
  agendarSalvamento();
};

onClickOutside(gaveta, () => card.value && fechar(), { ignore: [lista] });
useEventListener(document, 'keydown', evento => {
  if (evento.key === 'Escape' && card.value && conflito.value === null)
    fechar();
});

watch(busca, () => carregarLista());

onBeforeRouteLeave(() => {
  if (!sujo.value) return true;
  pedirDecisao(null);
  return false;
});

// As telas de Configurações vivem dentro de um <keep-alive>: sair e voltar NÃO remonta o
// componente, então `onMounted` não roda de novo e a tela volta com o estado de antes. Depois de
// publicar, isso significa uma tela achando que ainda existe um rascunho que já subiu, e todo
// salvamento batendo em 422. Recarregar ao reativar é o que mantém a tela e o servidor falando
// da mesma coisa.
let jaMontou = false;

onActivated(() => {
  if (!jaMontou) {
    jaMontou = true;
    return;
  }
  carregar();
});

onMounted(carregar);
</script>

<template>
  <SettingsLayout
    :is-loading="carregando"
    :loading-message="$t('BASE_EDITAR.HEADER')"
  >
    <template #body>
      <div class="flex flex-col gap-3.5 w-full min-w-0">
        <!-- Some sozinho quando termina. Fica no topo porque é a resposta para "importei e o
             Bira não acha nada", que é a pergunta que a espera invisível produzia. -->
        <div
          v-if="indexando"
          class="rounded-md border border-n-amber-6 bg-n-amber-2 px-[18px] py-2.5 flex flex-wrap items-center gap-x-2 gap-y-1"
        >
          <span class="text-[12.5px] font-medium text-n-amber-11">
            {{
              $t('BASE_EDITAR.INDEXANDO.TITULO', {
                feitos: (indexacao.total || 0) - (indexacao.pendentes || 0),
                total: indexacao.total || 0,
              })
            }}
          </span>
          <span class="text-[12.5px] text-n-slate-11">
            {{ $t('BASE_EDITAR.INDEXANDO.CORPO') }}
          </span>
        </div>
        <!-- A volta e o título na mesma linha: sozinho no topo, o "voltar" não tinha peso de
             cabeçalho e a tela parecia começar no meio. -->
        <div class="flex items-baseline gap-2.5">
          <button
            type="button"
            class="text-[15px] text-n-slate-11 hover:text-n-slate-12"
            @click="irPara('capitao_base')"
          >
            {{ $t('BASE_EDITAR.VOLTAR') }}
          </button>
          <h1 class="text-lg font-semibold text-n-slate-12">
            {{ $t('BASE_EDITAR.HEADER') }}
          </h1>
        </div>

        <!-- Barra do rascunho. Sticky e acima da gaveta (z-30 contra z-20): Salvar rascunho,
             Ver o que muda e Publicar são o que a pessoa procura DEPOIS de editar, e some-los
             atrás da gaveta ou da rolagem é escondê-los na hora exata em que são procurados. -->
        <div
          class="sticky top-0 z-30 rounded-md border border-n-amber-6 bg-n-amber-2 px-[18px] py-3.5 flex flex-wrap items-start gap-x-4 gap-y-2"
        >
          <div class="flex-1 min-w-[300px]">
            <div class="flex items-center gap-2.5 flex-wrap">
              <span
                v-if="rascunho.aberto"
                :class="MONO"
                class="bg-n-amber-3 text-n-amber-11"
              >
                {{ $t('BASE_EDITAR.ETIQUETA') }}
              </span>
              <input
                v-if="editandoRotulo && rascunho.aberto"
                v-model="rotulo"
                type="text"
                class="reset-base text-sm font-semibold text-n-slate-12 bg-transparent border-b border-dashed border-n-amber-6 focus:outline-none"
                @blur="salvarRotulo"
                @keyup.enter="salvarRotulo"
              />
              <button
                v-else-if="rascunho.aberto"
                type="button"
                class="text-sm font-semibold text-n-slate-12 border-b border-dashed border-n-amber-6"
                @click="editandoRotulo = true"
              >
                {{ rotulo || $t('BASE_EDITAR.SEM_ROTULO') }}
              </button>
              <!-- Sem rascunho aberto a barra não inventa um: dizer "rascunho · 0 alteradas"
                   seria afirmar que existe uma versão em andamento que ninguém começou. -->
              <span v-else class="text-sm font-medium text-n-slate-11">
                {{ $t('BASE_EDITAR.SEM_RASCUNHO') }}
              </span>
            </div>
            <p class="mt-1 text-xs text-n-amber-11/70">
              <template v-if="rascunho.aberto">
                {{ $t('BASE_EDITAR.META', { n: pendentes }) }}
                <template v-if="quemMexeu"> · {{ quemMexeu }}</template>
                ·
              </template>
              {{ $t('BASE_EDITAR.VIRA_VERSAO') }}
            </p>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <span
              v-if="ultimoSalvamento"
              class="font-mono text-[10.5px] tracking-[.06em] text-n-amber-11/70"
            >
              {{ $t('BASE_EDITAR.SALVO_AS', { hora: hora(ultimoSalvamento) }) }}
            </span>
            <Button
              :label="$t('BASE_EDITAR.SALVAR_RASCUNHO')"
              variant="faded"
              color="amber"
              size="sm"
              :disabled="!rascunho.aberto"
              @click="salvarRotulo"
            />
            <Button
              :label="$t('BASE_EDITAR.PUBLICAR')"
              color="amber"
              size="sm"
              :disabled="pendentes === 0"
              @click="irPara('capitao_base_publicar')"
            />
          </div>
        </div>

        <!-- Busca e filtros. Com a lista agrupada, filtrar é a forma normal de estreitar, e
             filtro escondido atrás de um botão vira filtro que ninguém usa. -->
        <div class="flex items-center gap-2.5">
          <input
            v-model="busca"
            type="search"
            class="reset-base w-[420px] h-[34px] rounded-[5px] border border-solid border-n-strong bg-n-solid-2 px-3 text-[13px] text-n-slate-12"
            :placeholder="$t('BASE_EDITAR.BUSCA')"
          />
          <FiltroSelect
            v-model="filtroCategoria"
            :rotulo="$t('BASE_EDITAR.CATEGORIA_ROTULO')"
            :opcoes="opcoesFiltroCategoria"
          />
          <FiltroSelect
            v-model="filtroEstado"
            :rotulo="$t('BASE_EDITAR.ESTADO_ROTULO')"
            :opcoes="opcoesEstado"
          />
          <span class="font-mono text-[10px] tracking-[.06em] text-n-slate-10">
            {{
              $t('BASE_EDITAR.CONTADOR', {
                total: itens.length,
                alteradas: pendentes,
              })
            }}
          </span>
          <Button
            :label="$t('BASE_EDITAR.NOVA')"
            variant="faded"
            color="slate"
            size="sm"
            class="ml-auto"
            @click="novaResposta()"
          />
        </div>

        <!-- Aviso de alterações não salvas -->
        <div
          v-if="conflito !== null"
          class="rounded-md border border-n-amber-6 bg-n-amber-2 px-4 py-3"
        >
          <p class="text-sm font-medium text-n-slate-12">
            {{ $t('BASE_EDITAR.CONFLITO.TITULO') }}
          </p>
          <div class="flex gap-2 mt-2">
            <Button
              :label="$t('BASE_EDITAR.CONFLITO.DESCARTAR')"
              variant="faded"
              color="slate"
              size="sm"
              @click="resolverConflito('descartar')"
            />
            <Button
              :label="$t('BASE_EDITAR.CONFLITO.SALVAR')"
              color="amber"
              size="sm"
              @click="resolverConflito('salvar')"
            />
          </div>
        </div>

        <!-- Área de trabalho. A lista tem largura fixa e não acompanha a janela: o espaço à
             direita existe para a gaveta entrar sem empurrar nada. -->
        <div
          class="relative h-[calc(100vh-15rem)] min-h-[520px] overflow-hidden"
        >
          <div
            ref="lista"
            class="w-[880px] max-w-full h-full overflow-y-auto px-[30px] pb-6 flex flex-col gap-[18px] transition-[filter] duration-200"
            :class="card ? 'brightness-[.55]' : ''"
          >
            <div
              v-for="grupo in grupos"
              :key="grupo.chave"
              class="flex flex-col gap-2.5"
            >
              <h3
                class="sticky top-0 z-10 bg-n-surface-1 -mx-[30px] px-[30px] pt-1 pb-[7px] border-b border-n-weak flex items-baseline gap-3"
              >
                <span
                  class="font-mono text-[11px] uppercase tracking-[.08em] text-n-slate-12"
                >
                  {{ grupo.nome }}
                </span>
                <span class="font-mono text-[10.5px] text-n-slate-10">
                  {{
                    grupo.itens.length === 1
                      ? $t('BASE_EDITAR.GRUPO_UMA')
                      : $t('BASE_EDITAR.GRUPO_CONTAGEM', {
                          n: grupo.itens.length,
                        })
                  }}
                </span>
              </h3>
              <div class="grid grid-cols-2 gap-3">
                <button
                  v-for="item in grupo.itens"
                  :key="item.numero_card"
                  type="button"
                  class="text-left h-[104px] min-w-0 overflow-hidden rounded-md border bg-n-solid-1 px-3.5 py-3 flex flex-col gap-1.5"
                  :class="[
                    abertoNumero === item.numero_card
                      ? 'border-n-amber-6'
                      : 'border-n-strong',
                    item.ativo ? '' : 'opacity-45',
                  ]"
                  @click="abrir(item)"
                >
                  <span class="flex items-start justify-between gap-2">
                    <span
                      class="text-[13.5px] font-semibold text-n-slate-12 leading-[1.3] line-clamp-1 break-words min-w-0"
                    >
                      {{ item.titulo }}
                    </span>
                    <span
                      :class="[MONO, CHIP_ESTADO[item.estado]]"
                      class="flex-none"
                    >
                      {{
                        $t(`BASE_EDITAR.ESTADO.${item.estado.toUpperCase()}`)
                      }}
                    </span>
                  </span>
                  <span
                    v-if="item.trecho"
                    class="text-[12.5px] text-n-slate-11 leading-[1.45] line-clamp-2 break-words"
                  >
                    {{ item.trecho }}
                  </span>
                </button>
              </div>
            </div>
            <!-- Vazio que não explica por que está vazio faz a pessoa achar que a resposta
                 sumiu da base. Como categoria e estado filtram aqui, e a busca no servidor, dá
                 para dizer quantas a busca sozinha acharia. -->
            <div
              v-if="!grupos.length"
              class="py-6 flex flex-col items-start gap-2"
            >
              <p class="text-sm text-n-slate-11">
                {{ $t('BASE_EDITAR.VAZIO') }}
              </p>
              <p v-if="filtrando" class="text-[12.5px] text-n-slate-10">
                {{ $t('BASE_EDITAR.VAZIO_FILTROS', { n: itens.length }) }}
              </p>
              <Button
                v-if="filtrando"
                :label="$t('BASE_EDITAR.LIMPAR_FILTROS')"
                variant="faded"
                color="slate"
                size="sm"
                @click="limparFiltros"
              />
            </div>
          </div>

          <!-- Gaveta. Entra por cima da lista, que continua visível atrás: a pessoa não perde
               o lugar onde estava, e nada faz reflow ao abrir e fechar. -->
          <Transition name="gaveta">
            <div
              v-if="card"
              ref="gaveta"
              class="absolute top-0 right-0 bottom-0 z-20 w-[760px] max-w-full flex flex-col bg-n-solid-1 border-l border-solid border-n-strong shadow-[-24px_0_48px_rgba(0,0,0,.5)]"
            >
              <div
                class="flex items-start gap-3 px-[22px] py-4 border-b border-n-weak flex-none"
              >
                <div class="flex-1 min-w-0 flex flex-col gap-1.5">
                  <span class="flex items-center gap-2 flex-wrap">
                    <span
                      v-if="card.category"
                      :class="MONO"
                      class="border border-solid border-n-strong text-n-slate-11"
                    >
                      {{ card.category }}
                    </span>
                    <span
                      v-if="sujo"
                      :class="MONO"
                      class="bg-n-amber-3 text-n-amber-11"
                    >
                      {{ $t('BASE_EDITAR.NAO_SALVA') }}
                    </span>
                  </span>
                  <!-- A pergunta é o nome do card, não o conteúdo dele. Em corpo grande, um
                       contexto longo (e a base do clube tem vários de cinco linhas) empurrava
                       para fora da tela justamente os campos que a pessoa abriu o card para
                       editar. Tamanho de texto comum, peso médio: continua sendo o título, sem
                       tomar metade do painel. -->
                  <span
                    class="text-[13px] font-medium text-n-slate-12 leading-[1.35] break-words"
                  >
                    {{ card.question || $t('BASE_EDITAR.NOVA_TITULO') }}
                  </span>
                  <p
                    v-if="mexidaPorOutra"
                    class="text-[12.5px] text-n-amber-11 break-words"
                  >
                    {{
                      $t('BASE_EDITAR.OUTRA_PESSOA', {
                        nome: mexidaPorOutra.alterado_por,
                        quando: dataHora(mexidaPorOutra.alterado_em),
                      })
                    }}
                  </p>
                </div>
                <Button
                  :label="$t('BASE_EDITAR.DESATIVAR')"
                  variant="faded"
                  color="slate"
                  size="sm"
                  :disabled="!card.enabled || card.nova"
                  @click="desativar"
                />
                <Button
                  :label="$t('BASE_EDITAR.HISTORICO')"
                  variant="faded"
                  color="slate"
                  size="sm"
                  :disabled="card.nova"
                  @click="mostrarHistorico = !mostrarHistorico"
                />
                <Button
                  label="✕"
                  variant="ghost"
                  color="slate"
                  size="sm"
                  @click="fechar"
                />
              </div>

              <div class="flex-1 min-h-0 min-w-0 flex items-stretch">
                <div
                  v-if="mostrarHistorico"
                  class="flex-1 min-w-0 h-full overflow-y-auto px-5 py-4 flex flex-col gap-2"
                >
                  <p v-if="!historico.length" class="text-sm text-n-slate-11">
                    {{ $t('BASE_EDITAR.HISTORICO_VAZIO') }}
                  </p>
                  <div
                    v-for="revisao in historico"
                    :key="revisao.id"
                    class="rounded-[5px] border border-n-weak px-3 py-2"
                  >
                    <span
                      class="font-mono text-[10px] tracking-[.06em] text-n-slate-10"
                    >
                      {{ dataHora(revisao.quando) }} ·
                      {{ revisao.autor || $t('BASE_EDITAR.SEM_AUTOR') }}
                      <template v-if="revisao.pendente">
                        · {{ $t('BASE_EDITAR.PENDENTE_REV') }}</template
                      >
                      <template v-else-if="revisao.publicacao">
                        · {{ revisao.publicacao }}</template
                      >
                    </span>
                    <p
                      class="mt-1 text-[13px] text-n-slate-11 whitespace-pre-line break-words"
                    >
                      {{ revisao.dados.answer }}
                    </p>
                    <Button
                      v-if="!revisao.pendente"
                      :label="$t('BASE_EDITAR.TRAZER')"
                      variant="link"
                      color="amber"
                      size="sm"
                      class="mt-1"
                      @click="trazerDeVolta(revisao)"
                    />
                  </div>
                </div>

                <div v-else class="flex-1 min-w-0 flex">
                  <!-- No ar hoje. Largura FIXA, e a do rascunho é quem cresce: sem os dois
                       flex-basis declarados, quem decidia a divisão era o texto mais longo, e o
                       lado que a pessoa edita acabava com noventa pixels. -->
                  <div
                    class="flex-[0_0_340px] min-w-0 h-full overflow-y-auto px-5 py-4 border-r border-solid border-n-weak bg-n-alpha-1 flex flex-col gap-3"
                  >
                    <span
                      class="font-mono text-[10px] uppercase tracking-[.06em] text-n-slate-10 whitespace-nowrap"
                    >
                      {{ $t('BASE_EDITAR.NO_AR') }}
                    </span>
                    <p
                      v-if="noAr"
                      class="rounded-[5px] border border-solid border-n-strong bg-n-solid-2 px-3 py-3 text-[13px] leading-relaxed text-n-slate-11 whitespace-pre-line break-words max-h-[240px] overflow-y-auto"
                    >
                      {{ noAr.answer }}
                    </p>
                    <p v-else class="text-[13px] text-n-slate-11">
                      {{ $t('BASE_EDITAR.NUNCA_ESTEVE') }}
                    </p>
                    <dl
                      v-if="noAr"
                      class="grid grid-cols-[132px_1fr] gap-x-2.5 gap-y-1.5 text-[12px] flex-none"
                    >
                      <dt class="text-n-slate-10 whitespace-nowrap">
                        {{ $t('BASE_EDITAR.CATEGORIA') }}
                      </dt>
                      <dd class="min-w-0 break-words text-n-slate-12">
                        {{ noAr.category || '—' }}
                      </dd>
                      <dt class="text-n-slate-10 whitespace-nowrap">
                        {{ $t('BASE_EDITAR.VALE_PARA') }}
                      </dt>
                      <dd class="min-w-0 break-words text-n-slate-12">
                        {{
                          [
                            ...(noAr.planos || []),
                            ...(noAr.situacoes || []),
                          ].join(', ') || $t('BASE_EDITAR.TODOS')
                        }}
                      </dd>
                      <dt class="text-n-slate-10 whitespace-nowrap">
                        {{ $t('BASE_EDITAR.LITERAL') }}
                      </dt>
                      <dd class="min-w-0 text-n-slate-12">
                        {{
                          noAr.literal
                            ? $t('BASE_EDITAR.SIM')
                            : $t('BASE_EDITAR.NAO')
                        }}
                      </dd>
                      <dt class="text-n-slate-10 whitespace-nowrap">
                        {{ $t('BASE_EDITAR.DEPOIS') }}
                      </dt>
                      <dd class="min-w-0 break-words text-n-slate-12">
                        {{ rotuloAcao(noAr) }}
                      </dd>
                    </dl>
                  </div>

                  <!-- Cópia para o rascunho -->
                  <div
                    class="flex-1 min-w-0 h-full overflow-y-auto px-5 py-4 flex flex-col gap-3"
                  >
                    <span
                      class="font-mono text-[10px] uppercase tracking-[.06em] text-n-amber-11 whitespace-nowrap"
                    >
                      {{ $t('BASE_EDITAR.COPIA') }}
                    </span>
                    <input
                      v-model="card.question"
                      type="text"
                      class="reset-base w-full min-w-0 rounded-[5px] border border-solid border-n-strong bg-n-solid-2 px-2.5 py-1.5 text-[13px] text-n-slate-12"
                      @input="agendarSalvamento"
                    />
                    <textarea
                      v-model="card.answer"
                      class="m-0 border-solid w-full min-w-0 min-h-[150px] resize-y leading-relaxed rounded-[5px] border border-n-amber-6 bg-n-solid-2 px-2.5 py-2 text-[13px] text-n-slate-12"
                      @input="agendarSalvamento"
                    />
                    <!-- Uma grade só para os três campos: em linhas separadas os rótulos nunca
                       caíam no mesmo eixo e o campo vinha empurrado para a direita. -->
                    <div
                      class="grid grid-cols-[130px_1fr] items-center gap-x-2.5 gap-y-2"
                    >
                      <span
                        class="text-[12.5px] text-n-slate-11 whitespace-nowrap"
                      >
                        {{ $t('BASE_EDITAR.CATEGORIA') }}
                      </span>
                      <FiltroSelect
                        v-model="card.category"
                        :opcoes="opcoesCategoria"
                        :placeholder="$t('BASE_EDITAR.CATEGORIA_VAZIA')"
                        largura-cheia
                        @update:model-value="agendarSalvamento"
                      />
                      <span
                        class="text-[12.5px] text-n-slate-11 whitespace-nowrap"
                      >
                        {{ $t('BASE_EDITAR.DEPOIS') }}
                      </span>
                      <FiltroSelect
                        v-model="acaoTipo"
                        :opcoes="opcoesAcao"
                        largura-cheia
                      />
                      <span
                        class="text-[12.5px] text-n-slate-11 whitespace-nowrap"
                      >
                        {{ $t('BASE_EDITAR.LITERAL') }}
                      </span>
                      <div>
                        <Switch
                          v-model="card.literal"
                          @change="agendarSalvamento"
                        />
                      </div>
                    </div>

                    <!-- T-216: o que o Bira precisa saber para ADAPTAR o texto. Só aparece
                         quando o card não é literal, porque no literal ele não adapta nada. -->
                    <div v-if="!card.literal" class="flex flex-col gap-2">
                      <input
                        v-model="card.pergunta_triagem"
                        type="text"
                        class="reset-base w-full min-w-0 rounded-[5px] border border-n-strong bg-n-solid-2 px-2.5 py-1.5 text-[13px] text-n-slate-12"
                        :placeholder="$t('BASE_EDITAR.PERGUNTA_TRIAGEM')"
                        @change="agendarSalvamento"
                      />
                      <input
                        v-model="card.condicao"
                        type="text"
                        class="reset-base w-full min-w-0 rounded-[5px] border border-n-strong bg-n-solid-2 px-2.5 py-1.5 text-[13px] text-n-slate-12"
                        :placeholder="$t('BASE_EDITAR.CONDICAO')"
                        @change="agendarSalvamento"
                      />
                      <p
                        v-if="faltaNoCardNaoLiteral.length"
                        class="text-[12.5px] text-n-amber-11"
                      >
                        {{
                          $t('BASE_EDITAR.FALTA_NAO_LITERAL', {
                            campos: faltaNoCardNaoLiteral
                              .map(c => $t(`BASE_EDITAR.CAMPO.${c}`))
                              .join(' e '),
                          })
                        }}
                      </p>
                    </div>

                    <div>
                      <button
                        type="button"
                        class="text-xs text-n-amber-11 underline"
                        @click="restringindo = !restringindo"
                      >
                        {{ $t('BASE_EDITAR.RESTRINGIR') }}
                      </button>
                      <p class="text-[12.5px] text-n-slate-10 mt-1 break-words">
                        {{ $t('BASE_EDITAR.ALCANCE', { alcance }) }}
                      </p>
                      <div v-if="restringindo" class="mt-2 flex flex-col gap-2">
                        <input
                          :value="(card.planos || []).join(', ')"
                          type="text"
                          class="reset-base w-full min-w-0 rounded-[5px] border border-n-strong bg-n-solid-2 px-2.5 py-1.5 text-[13px] text-n-slate-12"
                          :placeholder="$t('BASE_EDITAR.PLANOS')"
                          @change="definirLista('planos', $event.target.value)"
                        />
                        <input
                          :value="(card.situacoes || []).join(', ')"
                          type="text"
                          class="reset-base w-full min-w-0 rounded-[5px] border border-n-strong bg-n-solid-2 px-2.5 py-1.5 text-[13px] text-n-slate-12"
                          :placeholder="$t('BASE_EDITAR.SITUACOES')"
                          @change="
                            definirLista('situacoes', $event.target.value)
                          "
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Rodapé preso -->
              <div
                class="flex items-center gap-3 px-5 py-3 border-t border-n-weak bg-n-solid-2 flex-none"
              >
                <span
                  class="text-[12.5px] flex-1 min-w-0"
                  :class="sujo ? 'text-n-amber-11' : 'text-n-slate-10'"
                >
                  {{
                    card.nova
                      ? $t('BASE_EDITAR.NOVA_AJUDA')
                      : sujo
                        ? $t('BASE_EDITAR.PENDENTE')
                        : $t('BASE_EDITAR.NADA_ALTERADO')
                  }}
                </span>
                <Button
                  v-if="sujo"
                  :label="$t('BASE_EDITAR.DESCARTAR')"
                  variant="faded"
                  color="slate"
                  size="sm"
                  @click="descartar"
                />
                <!-- Desabilitado em âmbar continuava parecendo clicável. Cinza apagado diz o que é. -->
                <Button
                  :label="
                    card.nova
                      ? $t('BASE_EDITAR.CRIAR')
                      : $t('BASE_EDITAR.SALVAR')
                  "
                  :color="podeSalvar ? 'amber' : 'slate'"
                  :variant="podeSalvar ? 'solid' : 'faded'"
                  size="sm"
                  :is-loading="salvando"
                  :disabled="!podeSalvar"
                  @click="salvar(true)"
                />
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>

<style scoped>
/* 180ms ease-out: rápido o bastante para não atrasar quem trabalha, longo o bastante para a
   pessoa ver de onde a gaveta veio e entender que a lista continua ali atrás. */
.gaveta-enter-active,
.gaveta-leave-active {
  transition: transform 180ms ease-out;
}

.gaveta-enter-from,
.gaveta-leave-to {
  transform: translateX(100%);
}
</style>
